<template>
  <v-container fluid class="direct-payment-page">
    <!-- Encabezado -->
    <v-card class="page-header mb-4" elevation="3">
      <div class="header-content">
        <div class="d-flex align-center">
          <v-avatar size="56" color="white" class="mr-4 elevation-2">
            <v-icon size="32" color="primary">mdi-cash-fast</v-icon>
          </v-avatar>
          <div>
            <h1 class="page-title">Prepagos a suplidores</h1>
            <p class="page-subtitle">Pagos anticipados pendientes de aplicar a facturas</p>
          </div>
        </div>
        <v-spacer></v-spacer>
        <div class="header-actions">
          <v-btn
            v-if="hasFeature('CXP-022')"
            color="white"
            class="text-primary action-btn"
            elevation="2"
            @click="openPaymentDialog(1)"
          >
            <v-icon left color="success">mdi-cash-plus</v-icon>
            Nuevo prepago
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Filtros -->
    <v-card class="filters-card mb-4" outlined>
      <v-card-text class="pa-3">
        <search-header
          @dataFilters="changeBusiness($event)"
          :selected_week="2"
          :type="false"
        />
      </v-card-text>
    </v-card>

    <!-- Resumen: los montos se agrupan por moneda, nunca se suman monedas distintas -->
    <v-row class="mb-4" dense>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card" outlined>
          <div class="kpi-icon-wrapper kpi-blue">
            <v-icon color="white" size="28">mdi-format-list-numbered</v-icon>
          </div>
          <div class="kpi-content">
            <div class="kpi-label">Prepagos</div>
            <div class="kpi-value">{{ direct_payments.length }}</div>
            <div class="kpi-subtitle">
              {{ availableCount }} con saldo por consumir
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col
        v-for="summary in totalsByCurrency"
        :key="summary.code"
        cols="12"
        sm="6"
        md="3"
      >
        <v-card class="kpi-card" outlined>
          <div class="kpi-icon-wrapper kpi-orange">
            <v-icon color="white" size="28">mdi-progress-clock</v-icon>
          </div>
          <div class="kpi-content">
            <div class="kpi-label">Por consumir · {{ summary.code }}</div>
            <div class="kpi-value text-truncate">
              {{ currencyFormat(summary.pending) }}
            </div>
            <div class="kpi-subtitle text-truncate">
              de {{ currencyFormat(summary.total) }} prepagado ({{ summary.count }})
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Listado -->
    <v-card class="data-card" elevation="2">
      <v-toolbar flat class="data-toolbar">
        <v-icon left color="primary">mdi-table</v-icon>
        <v-toolbar-title class="text-subtitle-1 font-weight-medium">
          Listado
        </v-toolbar-title>
        <v-chip x-small color="primary" outlined class="ml-2 font-weight-medium">
          {{ direct_payments.length }}
        </v-chip>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          label="Buscar..."
          outlined
          dense
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          class="search-field"
        />
      </v-toolbar>

      <v-divider></v-divider>

      <v-data-table
        :search="search"
        :headers="headers"
        :items="direct_payments"
        :loading="loading"
        loading-text="Cargando prepagos..."
        sort-by="payment_date"
        sort-desc
        class="elevation-0 modern-table"
        :items-per-page="15"
        :footer-props="{
          itemsPerPageOptions: [10, 15, 25, 50, -1],
          itemsPerPageText: 'Filas por página',
          itemsPerPageAllText: 'Todos',
        }"
        no-results-text="No hay prepagos que coincidan con la búsqueda"
      >
        <template v-slot:[`item.prepayment_id`]="{ item }">
          <v-hover v-slot="{ hover }">
            <div
              @click="showDirectPayment(item.prepayment_id)"
              class="payment-id-cell"
              :class="{ 'payment-id-hover': hover }"
            >
              <v-icon left small color="primary">mdi-pound</v-icon>
              <span>{{ filLeft(item.prepayment_id.toString(), 8, "0") }}</span>
            </div>
          </v-hover>
        </template>

        <template v-slot:[`item.payment_date`]="{ item }">
          <div class="date-cell text-no-wrap">
            <v-icon x-small color="grey">mdi-calendar</v-icon>
            <span class="ml-1">{{ formatDate(item.payment_date) }}</span>
          </div>
        </template>

        <template v-slot:[`item.entity_name`]="{ item }">
          <div class="entity-cell">
            <v-avatar size="28" color="blue lighten-2" class="mr-2 white--text flex-shrink-0">
              <span class="font-weight-bold caption">
                {{ entityInitials(item.entity_name) }}
              </span>
            </v-avatar>
            <div class="entity-text">
              <div class="entity-name">
                {{ item.entity_name || "— Sin suplidor —" }}
              </div>
              <div v-if="item.note" class="caption grey--text text-truncate">
                {{ item.note }}
              </div>
            </div>
          </div>
        </template>

        <template v-slot:[`item.payment_type_name`]="{ item }">
          <v-chip x-small label outlined color="blue-grey">
            {{ item.payment_type_name || "—" }}
          </v-chip>
        </template>

        <template v-slot:[`item.business_unit_description`]="{ item }">
          <div class="unit-cell">
            <div>{{ item.business_unit_description }}</div>
            <div class="caption grey--text">{{ item.location_description }}</div>
          </div>
        </template>

        <template v-slot:[`item.total_amount`]="{ item }">
          <div class="amount-cell">
            <span class="amount-value font-weight-bold">
              {{ currencyFormat(item.total_amount) }}
            </span>
            <span class="amount-currency caption grey--text">
              {{ item.currency_code }}
            </span>
          </div>
        </template>

        <template v-slot:[`item.consumed`]="{ item }">
          <div class="consumed-cell">
            <div class="d-flex justify-space-between align-center mb-1">
              <span class="caption">{{ currencyFormat(item.consumed) }}</span>
              <span class="caption font-weight-medium" :class="consumedPercentClass(item)">
                {{ consumedPercent(item) }}%
              </span>
            </div>
            <v-progress-linear
              :value="consumedPercent(item)"
              :color="consumedColor(item)"
              height="4"
              rounded
            />
          </div>
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <v-chip x-small :color="status(item).color" text-color="white" class="font-weight-medium">
            {{ status(item).text }}
          </v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex justify-end">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  @click="showDirectPayment(item.prepayment_id)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>Ver detalle</span>
            </v-tooltip>
            <v-tooltip top v-if="item.consumed == 0 && hasFeature('CXP-022')">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="error"
                  @click="deleteItem(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>mdi-close-circle</v-icon>
                </v-btn>
              </template>
              <span>Anular</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="empty-state py-10">
            <v-icon size="80" color="grey lighten-1">mdi-database-off-outline</v-icon>
            <h3 class="mt-4 grey--text text--darken-1">No hay prepagos</h3>
            <p class="caption grey--text">
              No se encontraron prepagos con los filtros seleccionados
            </p>
            <v-btn v-if="hasFeature('CXP-022')" color="primary" outlined small class="mt-2" @click="openPaymentDialog(1)">
              <v-icon left small>mdi-plus</v-icon>
              Nuevo prepago
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Diálogo de pago -->
    <v-dialog transition="slide-x-transition" v-model="dialog" fullscreen persistent>
      <PaymentDialog
        :dialog="dialog"
        :type="paymentType"
        :business-units="$store.state.auth.bussines_units || []"
        :locations="$store.state.auth.locations || []"
        :departments="itemsDepartment || []"
        :payments-type="paymentsType || []"
        :currencies="currencies || []"
        :entity-payment-types="[]"
        :category-dgii="categoryDgii || []"
        :accounts="accounts || []"
        :filters="filters"
        :categories="categories || []"
        @close="closePaymentDialog"
        @saved="onPaymentSaved"
        @update-currencies="updateCurrencies"
      />
    </v-dialog>

    <!-- Confirmar anulación -->
    <v-dialog v-model="dialogDelete" max-width="460px" :persistent="deleting">
      <v-card>
        <v-card-title class="text-h6 error--text">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          Anular prepago
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text v-if="itemToDelete" class="pt-4">
          <div class="mb-3">
            ¿Deseas anular este prepago? <strong>Esta acción no se puede deshacer.</strong>
          </div>
          <div class="delete-summary">
            <div class="d-flex justify-space-between">
              <span class="grey--text">Prepago</span>
              <strong>#{{ filLeft(itemToDelete.prepayment_id.toString(), 8, "0") }}</strong>
            </div>
            <div class="d-flex justify-space-between">
              <span class="grey--text">Suplidor</span>
              <strong class="text-right ml-4">{{ itemToDelete.entity_name }}</strong>
            </div>
            <div class="d-flex justify-space-between">
              <span class="grey--text">Monto</span>
              <strong>
                {{ currencyFormat(itemToDelete.total_amount) }} {{ itemToDelete.currency_code }}
              </strong>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn text :disabled="deleting" @click="closeDelete">Volver</v-btn>
          <v-btn color="error" depressed :loading="deleting" @click="deleteItemConfirm">
            <v-icon left small>mdi-close-circle</v-icon>
            Anular prepago
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from "moment";
import PaymentDialog from "@/components/PaymentDialog.vue";

export default {
  components: {
    PaymentDialog,
  },
  data: () => ({
    search: "",
    loading: false,
    dialog: false,
    filters: null,
    dialogDelete: false,
    deleting: false,
    paymentType: 1, // 1: pagos, 2: cobros
    direct_payments: [],
    itemsDepartment: [],
    paymentsType: [],
    currencies: [],
    categoryDgii: [],
    accounts: [],
    categories: [],
    itemToDelete: null,
    headers: [
      { text: "Prepago", align: "start", value: "prepayment_id" },
      { text: "Fecha", value: "payment_date" },
      { text: "Suplidor", value: "entity_name" },
      { text: "Forma de pago", value: "payment_type_name" },
      { text: "Unidad / Sucursal", value: "business_unit_description" },
      { text: "Total", value: "total_amount", align: "end" },
      { text: "Consumido", value: "consumed", width: "170px" },
      { text: "Estado", value: "status", sortable: false },
      { text: "", value: "actions", sortable: false, align: "end" },
    ],
  }),
  computed: {
    totalsByCurrency() {
      const groups = {};
      for (const p of this.direct_payments) {
        const code = p.currency_code || "—";
        const total = parseFloat(p.total_amount || 0);
        const consumed = parseFloat(p.consumed || 0);
        if (!groups[code]) groups[code] = { code, total: 0, pending: 0, count: 0 };
        groups[code].total += total;
        groups[code].pending += Math.max(0, total - consumed);
        groups[code].count++;
      }
      return Object.values(groups).sort((a, b) => b.count - a.count);
    },
    availableCount() {
      return this.direct_payments.filter((p) => this.consumedPercent(p) < 100).length;
    },
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      try {
        this.itemsDepartment = await this.g_departments();

        const resultTypePayments = await this.api_Get("income/payments_types");
        this.paymentsType = await resultTypePayments.json();

        let resultTypeCategories = await this.api_Get("accounting/categories_diary_book");
        this.categories = await resultTypeCategories.json();

        let resultTypeAccounts = await this.api_Get("accounting/accountsUsable");
        const accountsData = await resultTypeAccounts.json();
        // Las cuentas de sistema solo se usan en entradas de diario: se muestran pero no se pueden elegir
        this.accounts = accountsData.map((element) => ({
          name: `${element.code} - ${element.name}${element.is_system_only ? " (sistema)" : ""}`,
          id: element.id,
          dgii_category_id: element.dgii_category_id,
          disabled: !!element.is_system_only,
        }));

        let resultCategoryDgii = await this.api_Get("accounting/category_dgii");
        const dataCategoryDgii = await resultCategoryDgii.json();

        this.categoryDgii = [];
        for (let item of dataCategoryDgii) {
          this.categoryDgii.push({ text: item.name, value: item.id });
        }

        // Formatear departments si es necesario
        this.itemsDepartment = this.itemsDepartment.map((dept) => ({
          text: dept.text || dept.name,
          value: dept.value || dept.id,
        }));
      } catch (error) {
        console.error("Error inicializando datos:", error);
        this.$toast.error("Error cargando datos iniciales");
      }
    },

    formatDate(date) {
      return date ? moment(date).format("DD/MM/YYYY") : "—";
    },
    entityInitials(name) {
      if (!name) return "?";
      const parts = name.trim().split(/\s+/);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0][0] + parts[1][0]).toUpperCase();
    },
    consumedPercent(item) {
      const total = parseFloat(item.total_amount || 0);
      const consumed = parseFloat(item.consumed || 0);
      if (total <= 0) return 0;
      return Math.min(100, Math.round((consumed / total) * 100));
    },
    consumedColor(item) {
      const pct = this.consumedPercent(item);
      if (pct === 0) return "grey lighten-1";
      if (pct < 100) return "blue";
      return "success";
    },
    consumedPercentClass(item) {
      const pct = this.consumedPercent(item);
      if (pct === 0) return "grey--text";
      if (pct < 100) return "blue--text text--darken-1";
      return "success--text text--darken-1";
    },
    status(item) {
      const pct = this.consumedPercent(item);
      if (pct === 0) return { text: "Libre", color: "orange darken-1" };
      if (pct < 100) return { text: "Parcial", color: "blue" };
      return { text: "Consumido", color: "success" };
    },

    openPaymentDialog(type) {
      this.paymentType = type;
      this.dialog = true;
    },

    closePaymentDialog() {
      this.dialog = false;
    },

    onPaymentSaved(data) {
      this.$toast.success(
        `${this.paymentType === 1 ? "Pago" : "Cobro"} guardado exitosamente`
      );
      this.showDirectPayment(data[0].id);
      this.reload();
    },

    updateCurrencies(currencies) {
      this.currencies = currencies;
    },

    showDirectPayment(id) {
      let route = this.$router.resolve({
        name: "ViewDirectPaymentCxp",
        params: { id: id },
      });
      window.open(route.href);
    },

    async getData(data) {
      this.loading = true;
      let filters = "where invoice_type_id in (1,4)";
      filters +=
        data.business == null ? " " : " and BUSINESS_UNIT_ID in (" + data.business + ") ";
      filters +=
        data.location == null || data.location == 0
          ? ""
          : " AND LOCATION_ID IN (" + data.location + ")";
      filters +=
        data.department_id == null || data.department_id == 0
          ? ""
          : " AND DEPARTMENT_ID =" + data.department_id + "";
      filters +=
        " AND PAYMENT_DATE::DATE BETWEEN '" +
        data.dateStart +
        "' and '" +
        data.dateEnd +
        "' ";

      try {
        let result = await this.api_Post("transactions/all_direct_payments", {
          filters: filters,
        });
        const rows = await result.json();
        this.direct_payments = Array.isArray(rows) ? rows : [];
      } catch (error) {
        console.error(error);
        this.$toast.error("Error cargando prepagos");
      } finally {
        this.loading = false;
      }
    },

    changeBusiness(data) {
      if (data.business == 0) {
        this.getData({
          business: this.$store.state.auth.user.acess_business_unit_finance,
          location: null,
          dateStart: data.dateStart,
          dateEnd: data.dateEnd,
        });
      } else {
        this.getData(data);
      }
      this.filters = data;
    },

    // Recarga la lista con los filtros actuales (incluye el caso "todas las unidades")
    reload() {
      if (this.filters) this.changeBusiness(this.filters);
    },

    deleteItem(item) {
      this.itemToDelete = item;
      this.dialogDelete = true;
    },

    async deleteItemConfirm() {
      if (!this.itemToDelete || this.deleting) return;
      this.deleting = true;
      try {
        const responseRequest = await this.api_Post(
          "transactions/remove_payment_direct",
          { id: this.itemToDelete.prepayment_id }
        );
        if (responseRequest.status == 200) {
          this.$toast.success("Prepago anulado");
          this.reload();
        } else {
          this.$toast.error("Error al anular pago");
        }
      } finally {
        this.deleting = false;
        this.closeDelete();
      }
    },

    closeDelete() {
      // itemToDelete se conserva para que el diálogo no se vacíe durante la animación de cierre
      this.dialogDelete = false;
    },
    filLeft(str, length, char) {
      return str.padStart(length, char);
    },
  },
};
</script>

<style scoped>
.direct-payment-page {
  background-color: #f4f6f9;
  min-height: 100vh;
  padding: 16px;
}

/* Encabezado */
.page-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  border-radius: 12px !important;
  overflow: hidden;
}

.header-content {
  display: flex;
  align-items: center;
  padding: 20px 28px;
  color: white;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  color: white;
  letter-spacing: 0.5px;
}

.page-subtitle {
  font-size: 13px;
  margin: 2px 0 0;
  color: rgba(255, 255, 255, 0.85);
}

.action-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px;
}

.text-primary {
  color: #1976d2 !important;
}

.filters-card {
  border-radius: 10px !important;
  border-color: #e0e0e0 !important;
}

/* Tarjetas de resumen */
.kpi-card {
  border-radius: 10px !important;
  border-color: #e0e0e0 !important;
  display: flex;
  align-items: center;
  padding: 16px;
  height: 100%;
  transition: all 0.25s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
}

.kpi-icon-wrapper {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  flex-shrink: 0;
}

.kpi-blue { background: linear-gradient(135deg, #2196f3, #1976d2); }
.kpi-orange { background: linear-gradient(135deg, #ffa726, #f57c00); }

.kpi-content {
  flex: 1;
  min-width: 0;
}

.kpi-label {
  font-size: 12px;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #212121;
  line-height: 1.2;
  margin-top: 2px;
}

.kpi-subtitle {
  font-size: 12px;
  color: #9e9e9e;
  margin-top: 2px;
}

/* Listado */
.data-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.data-toolbar {
  background-color: #fafbfc !important;
  border-bottom: 1px solid #e9ecef;
  padding: 0 16px;
}

.search-field {
  max-width: 280px;
}

.modern-table ::v-deep table thead tr th {
  background-color: #f8f9fa !important;
  color: #495057 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  border-bottom: 2px solid #e9ecef !important;
}

.modern-table ::v-deep table tbody tr {
  transition: background-color 0.2s ease;
}

.modern-table ::v-deep table tbody tr:hover {
  background-color: rgba(25, 118, 210, 0.04) !important;
}

.payment-id-cell {
  display: inline-flex;
  align-items: center;
  font-family: "Courier New", monospace;
  font-weight: 600;
  color: #424242;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.payment-id-hover {
  background-color: #e3f2fd;
  color: #1565c0 !important;
}

.date-cell {
  font-size: 13px;
  color: #616161;
}

.entity-cell {
  display: flex;
  align-items: center;
  padding: 6px 0;
}

.entity-text {
  min-width: 0;
  max-width: 280px;
}

.entity-name {
  font-weight: 500;
  color: #212121;
  font-size: 14px;
}

.unit-cell {
  font-size: 13px;
  line-height: 1.3;
}

.amount-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
}

.amount-value {
  font-size: 15px;
  font-family: "Roboto Mono", monospace;
  white-space: nowrap;
}

.amount-currency {
  font-size: 11px;
  margin-top: 2px;
}

.consumed-cell {
  min-width: 140px;
}

.delete-summary {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 10px 14px;
  line-height: 1.9;
}

.empty-state {
  text-align: center;
}

@media (max-width: 960px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  .header-actions {
    width: 100%;
  }
}

@media (max-width: 600px) {
  .search-field {
    max-width: none;
  }
}
</style>
